import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { productsAPI } from '../api/products';
import { Product, Category } from '../types';
import ProductCard from '../components/ui/ProductCard';
import { ProductGridSkeleton } from '../components/ui/SkeletonLoader';

const HomePage: React.FC = () => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuredRes, categoriesRes] = await Promise.all([
          productsAPI.getFeatured(),
          productsAPI.getCategories(),
        ]);
        if (featuredRes.success && featuredRes.data) {
          setFeaturedProducts(featuredRes.data as Product[]);
        }
        if (categoriesRes.success && categoriesRes.data) {
          setCategories(categoriesRes.data);
        }
      } catch (error) {
        console.error('Failed to fetch homepage data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="animate-fade-in">

{/* Hero Section */}
<section className="relative h-screen flex items-center justify-center overflow-hidden">
  <div className="absolute inset-0">
    <img
      src="https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTR8fGNsb3RoaW5nfGVufDB8fDB8fHww"
      alt="Lanre Savage Collection"
      className="w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-black/30" />
  </div>
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 1, ease: 'easeOut' }}
    className="text-center z-10 px-6"
  >
    <motion.p
      initial={{ opacity: 0, letterSpacing: '0.5em' }}
      animate={{ opacity: 1, letterSpacing: '0.3em' }}
      transition={{ duration: 1.2, delay: 0.2 }}
      className="text-xs uppercase text-white/80 mb-6 tracking-widest"
    >
      New Collection 2026
    </motion.p>
    <motion.h1
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 0.4 }}
      className="font-serif text-6xl md:text-8xl text-white mb-8 leading-none"
    >
      Lanre Savage
    </motion.h1>
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, delay: 0.6 }}
      className="text-sm text-white/80 tracking-widest uppercase mb-12 max-w-md mx-auto"
    >
      Timeless luxury for the modern individual
    </motion.p>
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.8 }}
      className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6"
    >
      <Link to="/shop" className="bg-white text-primary px-10 py-4 text-xs tracking-widest uppercase hover:bg-nude transition-colors">
        Shop Collection
      </Link>
      <Link
        to="/about"
        className="text-xs tracking-widest uppercase text-white flex items-center space-x-2 hover:text-nude transition-colors"
      >
        <span>Our Story</span>
        <ArrowRight size={14} />
      </Link>
    </motion.div>
  </motion.div>

  {/* Scroll Indicator */}
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 1.5, duration: 0.8 }}
    className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center space-y-2"
  >
    <motion.div
      animate={{ y: [0, 8, 0] }}
      transition={{ duration: 1.5, repeat: Infinity }}
      className="w-px h-12 bg-white/50"
    />
    <span className="text-xs tracking-widest uppercase text-white/50">Scroll</span>
  </motion.div>
</section>

      {/* Categories Section */}
      {categories.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-widest uppercase text-nude mb-3">
              Explore
            </p>
            <h2 className="font-serif text-4xl text-primary">Collections</h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/shop?category=${category.slug}`}
                  className="block group"
                >
                  <div className="aspect-square bg-light overflow-hidden relative">
                    {category.image_url ? (
                      <img
                        src={category.image_url}
                        alt={category.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-600"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-light to-nude" />
                    )}
                    <div className="absolute inset-0 bg-primary/20 group-hover:bg-primary/40 transition-colors duration-300" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white font-serif text-xl tracking-widest uppercase">
                        {category.name}
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex items-end justify-between mb-12"
        >
          <div>
            <p className="text-xs tracking-widest uppercase text-nude mb-3">
              Curated
            </p>
            <h2 className="font-serif text-4xl text-primary">Featured Pieces</h2>
          </div>
          <Link
            to="/shop"
            className="text-xs tracking-widest uppercase text-primary flex items-center space-x-2 hover:text-nude transition-colors"
          >
            <span>View All</span>
            <ArrowRight size={14} />
          </Link>
        </motion.div>

        {isLoading ? (
          <ProductGridSkeleton count={4} />
        ) : featuredProducts.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {featuredProducts.slice(0, 4).map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-xs tracking-widest uppercase text-nude">
              Collection coming soon
            </p>
          </div>
        )}
      </section>

      {/* Brand Statement */}
      <section className="bg-light py-24 my-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mx-auto text-center px-6"
        >
          <p className="text-xs tracking-widest uppercase text-nude mb-6">
            Our Philosophy
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-primary leading-tight mb-8">
            Crafted for those who appreciate the art of dressing well
          </h2>
          <p className="text-sm text-secondary leading-relaxed tracking-wide mb-10">
            Each piece in the Lanre Savage collection is thoughtfully designed
            with the finest materials, combining contemporary aesthetics with
            timeless elegance.
          </p>
          <Link to="/about" className="btn-secondary">
            Discover Our Story
          </Link>
        </motion.div>
      </section>

      {/* New Arrivals Strip */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex items-end justify-between mb-12"
        >
          <div>
            <p className="text-xs tracking-widest uppercase text-nude mb-3">
              Just In
            </p>
            <h2 className="font-serif text-4xl text-primary">New Arrivals</h2>
          </div>
          <Link
            to="/shop?sort=newest"
            className="text-xs tracking-widest uppercase text-primary flex items-center space-x-2 hover:text-nude transition-colors"
          >
            <span>View All</span>
            <ArrowRight size={14} />
          </Link>
        </motion.div>

        {isLoading ? (
          <ProductGridSkeleton count={4} />
        ) : featuredProducts.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {featuredProducts.slice(0, 4).map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-xs tracking-widest uppercase text-nude">
              New arrivals coming soon
            </p>
          </div>
        )}
      </section>

      {/* Services Section */}
      <section className="border-t border-light py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { title: 'Free Shipping', desc: 'On orders over $300' },
              { title: 'Easy Returns', desc: '30-day return policy' },
              { title: 'Secure Payment', desc: 'SSL encrypted checkout' },
              { title: 'Customer Care', desc: 'Mon-Sat, 9am-6pm' },
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <h3 className="text-xs tracking-widest uppercase text-primary mb-2">
                  {service.title}
                </h3>
                <p className="text-xs text-nude tracking-wide">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;